import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  rem,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalOverlay,
  ModalFooter,
  Button,
  Select,
  SelectItem,
  SelectItemText,
} from '@vimeo/bokeh';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { AnyObject, Maybe } from '@vimeo/core/types/generic';
import { Logger, useLogger } from '@vimeo/logging';

import { GctlConfig, useGctlConfig } from '../../../api/base';
import { downloadInteractionHistoryByConnection } from '../../../api/interaction';
import { T_CANCEL, T_CURRENT_SESSION, T_DOWNLOAD } from '../../../translations';
import { TInteractionHistoryConnection } from '../../../types/interaction_tools';
import { createDomName } from '../../../utils/dom/identifying';
import { downloadFileByBlob } from '../../../utils/general/download';
import { exportDateFormatter } from '../../../utils/general/timing';

interface IDownloadModalProps extends IBaseDomElementProps {
  header: string;
  isOpen: boolean;
  prefix: string;
  history: Array<TInteractionHistoryConnection>;
  onDownload(connection: TInteractionHistoryConnection): void;
  onClose(): void;
}

export function DownloadModal({
  id = createDomName('download-modal'),
  header,
  prefix,
  isOpen,
  history,
  onDownload,
  onClose,
}: IDownloadModalProps) {
  const log: Logger = useLogger('DownloadModal');
  const gctlConfig: GctlConfig = useGctlConfig();

  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [selectedValue, setSelectedValue] = useState<string>(
    () => history.find((it) => !it.streamingOn)?.uri ?? '',
  );

  const selectItems: Array<{
    label: string;
    value: string;
  }> = useMemo(
    () =>
      history
        .sort((a, b) => (a.streamingOn && b.streamingOn ? b.streamingOn - a.streamingOn : 1))
        .map((it) => ({
          label: it.streamingOn
            ? exportDateFormatter(navigator.language).format(it.streamingOn * 1_000)
            : T_CURRENT_SESSION,
          value: it.uri,
        })),
    [history],
  );

  const availableItems = useMemo(
    () =>
      history.reduce((acc, it) => {
        (acc as any)[it.uri] = it;

        return acc;
      }, {}),
    [history],
  );

  // AnyObject used type here because it conflicts with standard ValueChangeDetails type for select which is not exported
  const onExportTargetChange = useCallback(
    ({ value }: AnyObject) => {
      setSelectedValue(value[0]);
    },
    [setSelectedValue],
  );

  const onDownLoadClicked = useCallback(async () => {
    const connection: Maybe<TInteractionHistoryConnection> = (availableItems as any)[selectedValue];

    if (connection) {
      log.info('Exporting history:', prefix, connection);

      onDownload(connection);

      setIsDownloading(true);

      try {
        const blob: Blob = await downloadInteractionHistoryByConnection({ connection, gctlConfig });
        const filename: string = `${prefix ? prefix + '_' : ''}${new Date(
          connection.streamingOn ? connection.streamingOn * 1000 : Date.now(),
        ).toLocaleString()}_${connection.id}.csv`;

        downloadFileByBlob(filename, blob);
      } catch (error) {
        log.error('Failed to load history item:', prefix, error, connection);
      } finally {
        setIsDownloading(false);
      }
    } else {
      log.error(
        'Failed to download exported item - no selected value for:',
        selectedValue,
        availableItems,
      );
    }
  }, [availableItems, selectedValue, log, prefix, onDownload, gctlConfig]);

  // Subscription to history changes if ast initial render in was empty
  useEffect(() => {
    if (!selectedValue) {
      setSelectedValue(history.find((it) => !it.streamingOn)?.uri ?? '');
    }
  }, [history, selectedValue]);

  return (
    <Modal id={id} returnFocusOnClose={false} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent width={rem(400)}>
        <ModalHeader id={createDomName(id, 'header')}>{header}</ModalHeader>

        <ModalBody paddingTop={rem(4)}>
          <Select
            id={createDomName(id, 'select')}
            className={createDomName(id, 'select')}
            size={'sm'}
            items={selectItems}
            value={selectedValue ? [selectedValue] : undefined}
            onValueChange={onExportTargetChange}
          >
            {(item) => (
              <SelectItem key={item.value} className={createDomName(id, 'select-option')}>
                <SelectItemText>{item.label}</SelectItemText>
              </SelectItem>
            )}
          </Select>
        </ModalBody>

        <ModalFooter>
          <Button
            id={createDomName(id, 'cancel-button')}
            size={'md'}
            variant={'secondary'}
            onClick={onClose}
          >
            {T_CANCEL}
          </Button>

          <Button
            id={createDomName(id, 'download-button')}
            size={'md'}
            variant={'primary'}
            isLoading={isDownloading}
            onClick={onDownLoadClicked}
          >
            {T_DOWNLOAD}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
